import { analysisActions, useCashFlowsPlot, useNodeById } from '@/stores/analysisStore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CashFlowsForm from './CashFlowsForm';
import CashFlowDataGrid from '@/components/datagrids/CashFlowDataGrid';
import CumulativeCashFlowChart from '@/components/charts/CumulativeCashFlowChart';
import InvestmentHeadline from './InvestmentHeadline';
import IncomeStatementTable from './IncomeStatementTable';
import { usePlotSync, usePlotSyncHandler } from '@/components/providers/PlotSyncProvider';
import { getLcohUnit, parseFinancialDeepDive } from '@/utils/analysisUtils';
import { unitToString } from '@/utils/unitUtils';

const CashFlowsPlot = () => {
  const plot = useCashFlowsPlot();
  const {
    results,
    nodeId,
    levelizeUnit,
    levelized_cost: levelizedCost,
    levelized_total: levelizedTotal,
    financial_results: financialResults,
  } = plot;
  // Prefer pure LCOH; fall back to hierarchy total for nodes without full DCFA.
  const lcohForKpi = levelizedCost ?? levelizedTotal ?? null;
  const node = useNodeById(nodeId);
  const [showDetail, setShowDetail] = useState(false);
  const { setParams } = usePlotSync();

  const updatePlot = useCallback(
    async formData => {
      setParams(formData);
      await analysisActions.updatePlot('cash_flows', { nodeId: formData.nodeId });
    },
    [setParams],
  );

  const defaultValues = useMemo(() => ({ nodeId }), [nodeId]);
  const { syncData } = usePlotSyncHandler(updatePlot, defaultValues);

  useEffect(() => {
    syncData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deepDive = useMemo(
    () => parseFinancialDeepDive(results?.data, financialResults, lcohForKpi),
    [results, financialResults, lcohForKpi],
  );

  const lcohUnit = getLcohUnit(levelizeUnit);
  const cashFlowUnitLabel = deepDive?.cashFlowUnit ? unitToString(deepDive.cashFlowUnit) : '$';
  const yearLabels = deepDive?.labels ?? [];

  return (
    <div className="relative flex flex-col gap-2">
      <CashFlowsForm key={nodeId} onSubmit={updatePlot} defaultValues={defaultValues} />

      {!deepDive && <div className="text-sm text-gray-500 py-12 text-center">Loading financial analysis…</div>}

      {deepDive && (
        <>
          <InvestmentHeadline kpis={deepDive.kpis} nodeLabel={node?.label} lcohUnit={lcohUnit} />

          <section className="py-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Cumulative cash flow</h3>
            <div className="h-[36vh]">
              <CumulativeCashFlowChart
                labels={yearLabels}
                annual={deepDive.annualNetCashFlow}
                cumulative={deepDive.cumulative}
                yAxisLabel={cashFlowUnitLabel}
                paybackYears={deepDive.kpis?.payback?.scalar ?? null}
              />
            </div>
          </section>

          <section className="py-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Income statement</h3>
            <IncomeStatementTable
              rows={deepDive.incomeRows}
              labels={yearLabels}
              operationYears={deepDive.operationYears}
            />
          </section>

          <section className="py-2">
            <button
              type="button"
              onClick={() => setShowDetail(prev => !prev)}
              className="w-full py-2 text-left text-sm font-semibold text-gray-900 flex justify-between items-center hover:text-gray-700"
              aria-expanded={showDetail}
              aria-controls="cash-flow-detail-panel"
            >
              <span>Full yearly cash flow detail</span>
              <span className="text-gray-500 text-xs">{showDetail ? 'Hide' : 'Show'}</span>
            </button>
            {showDetail && (
              <div id="cash-flow-detail-panel" className="pt-3 text-xs overflow-auto max-h-[40vh]">
                {results?.data && <CashFlowDataGrid data={results.data} />}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default CashFlowsPlot;
